import { Loading } from '@nike/frame-component-library';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { MSG_AUTH_EXPIRE } from '../../../constants/constants';
import { parseSLSandBrickworksErrors } from '../../../utils/formatting';
import { loadState } from '../../../utils/local-storage';
import { getStoreById, putStore } from '../../../utils/service-calls/sls';
import { isUserAllowed } from '../../../utils/tab-permissions';
import { ButtonBlack, StoreSelect } from '../../reusable';

import StoreDescription from './StoreDescription';
import StoreImage from './StoreImage';
import { centerAlignmentStyle, messageStyle, updateButtonContainerStyle } from './styles';

const Main = () => {
  const accessToken = useSelector((state) => state.authorizationReducer.auth.accessToken);
  const username = useSelector((state) => state.authorizationReducer.auth.username);
  const userGroups = useSelector((state) => state.authorizationReducer.auth.groups);
  const [selectedCountry, setSelectedCountry] = useState(loadState('auth')?.country);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedImageUrl, setEditedImageUrl] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [putting, setPutting] = useState(false);
  const [putError, setPutError] = useState('');
  const [putSuccess, setPutSuccess] = useState('');

  const selectCountry = (country) => {
    setSelectedCountry(country);
    setSelectedStore(null);
    setPutError('');
    setPutSuccess('');
  };

  const selectStore = (store) => {
    setSelectedStore(store);
    setStoreData(null);
    setPutError('');
    setPutSuccess('');
    setEditedDescription('');
  };

  useEffect(() => {
    const fetchStoreInfo = async () => {
      if (!selectedStore?.id) {
        setStoreData(null);
        return;
      }

      try {
        setLoading(true);
        const storeResponse = await getStoreById(accessToken, selectedStore.id);
        const store = storeResponse?.body;
        setStoreData(store);
        const imageUrl = store?.imageUrl;
        setEditedImageUrl(imageUrl || '');
        const description = store?.description;
        setEditedDescription(description || '');
      } catch (error) {
        setStoreData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStoreInfo();
  }, [selectedStore, accessToken]);

  const handleSubmit = async () => {
    // Validate store data exists
    if (!storeData) {
      setPutError('No store data available');
      return;
    }

    // Validate user permissions
    if (!isUserAllowed(userGroups, 'SLS.ALL') && !isUserAllowed(userGroups, `SLS.${storeData.region}`)
     && !isUserAllowed(userGroups, 'SLS.Media.ALL') && !isUserAllowed(userGroups, `SLS.Media.${storeData.region}`)) {
      setPutError(`User does not have permissions to perform actions in the region: ${storeData.region}`);
      return;
    }

    // Validate URL format
    if (editedImageUrl && !editedImageUrl.match(/^https?:\/\/.+/)) {
      setPutError('Please enter a valid URL starting with http:// or https://');
      return;
    }

    // Validate description length
    if (editedDescription && editedDescription.length > 255) {
      setPutError('Store description cannot exceed 255 characters');
      return;
    }

    // Confirm with user
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to update the store information?')) {
      return;
    }

    setPutting(true);
    setPutError('');
    setPutSuccess('');

    try {
      const updatedStoreData = {
        ...storeData,
        description: editedDescription,
        imageUrl: editedImageUrl,
      };

      await putStore(accessToken, { new: updatedStoreData, old: storeData });

      setPutSuccess('Successfully updated store information.');
      setStoreData(updatedStoreData);

      // Refetch to ensure we have the latest data
      const storeResponse = await getStoreById(accessToken, selectedStore.id);
      const store = storeResponse?.body;
      setStoreData(store);
      const imageUrl = store?.imageUrl;
      setEditedImageUrl(imageUrl || '');
      const description = store?.description;
      setEditedDescription(description || '');
    } catch (error) {
      const err = parseSLSandBrickworksErrors(error, true);

      if (err.timeout) {
        setPutError('PUT timed out. Please refresh and try again.');
      } else if (err.brickworks) {
        setPutSuccess('Successfully updated store information.');
        setPutError(`Brickworks write-back error ${err.statusCode}: ${err.message}`);
      } else if (err.message === MSG_AUTH_EXPIRE) {
        setPutError(`PUT failed with error: ${err.message}`);
      } else {
        setPutError(`PUT failed with error: ${err.message}`);
      }
    } finally {
      setPutting(false);
    }
  };

  return (
    <main className="ncss-col-sm-10">
      <StoreSelect
        selectCountry={selectCountry}
        selectStore={selectStore}
        selectedCountry={selectedCountry}
        selectedStore={selectedStore}
      />

      {putSuccess && (
        <div className="mt4-sm p3-sm bg-success-light text-color-success" style={messageStyle}>
          <p className="body-2 mb0-sm">{putSuccess}</p>
        </div>
      )}

      {putError && (
        <div className="mt4-sm p3-sm bg-error-light text-color-error" style={messageStyle}>
          <p className="body-2 mb0-sm">{putError}</p>
        </div>
      )}

      {selectedStore && (
        <>
          {loading && (
            <div className="mt4-sm" style={centerAlignmentStyle}>
              <Loading />
              <p className="body-2 mt2-sm">Loading store data...</p>
            </div>
          )}

          {putting && (
            <div className="mt4-sm" style={centerAlignmentStyle}>
              <Loading />
              <p className="body-2 mt2-sm">Updating store information...</p>
            </div>
          )}

          {!loading && !putting && storeData && (
            <>
              <StoreDescription
                description={editedDescription}
                onChange={setEditedDescription}
              />

              <StoreImage
                accessToken={accessToken}
                imageUrl={editedImageUrl}
                selectedStore={selectedStore}
                storeData={storeData}
                username={username}
                onChange={setEditedImageUrl}
              />

              <div style={updateButtonContainerStyle}>
                <ButtonBlack
                  disabled={putting}
                  label="Update"
                  onClick={handleSubmit}
                />
              </div>
            </>
          )}
        </>
      )}
    </main>
  );
};

export default Main;
